import styled, { css } from 'styled-components'

import { between } from 'ui-primitives/src/utils/between'
import { modularScale } from 'ui-primitives/src/utils/sizes'
import { spacing } from 'ui-primitives/src/utils/spacing'
import { Type } from 'bl-common/src/elements/Typography/Typography'

interface PickerProps {
  text?: string
  subtext?: string
  value: number
  disableIncrement?: boolean
  disableDecrement?: boolean
  hasError?: boolean
  min?: number
  max?: number
  onChange: (val: number) => void
  increaseAriaLabel?: string
  decreaseAriaLabel?: string
  isCompact?: boolean
  className?: string
  onDisabledIncrementClick?: (val: number) => void
  iconColor?: string
}

interface ButtonProps {
  isDisabled: boolean
}

interface ContainerProps {
  className?: string
  hasError?: boolean
}

interface SharedProps {
  isCompact?: boolean
}

const commonButtonStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;

  &:focus:not(:disabled) {
    outline: 1px solid var(--color-focus);
  }
`

const compactSize = css`
  height: 30px;
  width: 30px;
`

const scaleSize = (size: number) => between(size / modularScale, size)

const getSizeStyles = ({ isCompact }: { isCompact?: boolean }) =>
  isCompact
    ? compactSize
    : css`
        padding: ${scaleSize(16)} ${scaleSize(20)};
      `

const Container = styled.div<ContainerProps & SharedProps>`
  display: flex;
  width: 100%;
  border-radius: var(--border-radius);
  background: ${({ isCompact }) => (!isCompact ? 'var(--color-fillPrimary)' : 'none')};
  outline: ${({ hasError }) => (hasError ? '1px solid var(--color-error)' : 'none')};
  box-shadow: ${({ isCompact }) => (!isCompact ? 'var(--box-shadow)' : 'none')};
`

const TextWrap = styled.div<SharedProps>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0;
  flex-direction: column;
  ${({ isCompact }) => getSizeStyles({ isCompact })}
  ${({ isCompact }) =>
    isCompact
      ? css`
          padding-right: ${spacing[2]};
        `
      : css`
          padding-left: ${spacing[1.5]};
        `}
`

const Text = styled(Type)<SharedProps>`
  font-weight: normal;

  ${({ isCompact }) =>
    isCompact &&
    css`
      font-size: 16px;
    `}
`

const TextLabel = styled(Type)<SharedProps>`
  text-wrap: pretty;
  ${({ isCompact }) =>
    isCompact &&
    css`
      font-size: 14px;
    `}
`

const ButtonWrap = styled.div<SharedProps>`
  display: flex;
  ${({ isCompact }) =>
    isCompact &&
    css`
      border: 1px solid var(--border-color);
      height: 32px;
    `}
`

const Button = styled.button<ButtonProps & SharedProps>`
  ${commonButtonStyles}
  cursor: pointer;
  > svg {
    opacity: ${({ isDisabled }) => (isDisabled ? 0.33 : 1)};
  }

  ${({ isCompact }) =>
    isCompact
      ? compactSize
      : css`
          ${getSizeStyles({ isCompact })};
          border-left: 1px solid var(--border-color);
        `}
`

const CountWrap = styled.div<SharedProps>`
  align-items: center;
  display: flex;
  justify-content: center;
  border-left: 1px solid var(--border-color);

  > ${Type} {
    user-select: none;
  }

  ${({ isCompact }) =>
    isCompact
      ? css`
          ${compactSize};
          border-left: 1px solid var(--border-color);
          border-right: 1px solid var(--border-color);
        `
      : getSizeStyles({ isCompact })}
`

const Icon = ({
  type,
  color,
  height,
  width,
}: {
  type: 'plus' | 'minus'
  color?: string
  height?: number | string
  width?: number | string
}) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox={type === 'minus' ? '0 0 14 3' : '0 0 14 14'}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {type === 'minus' ? (
        <path
          d="M13.7523 2.32004V0.81604H0.696289V2.32004H13.7523Z"
          fill={color || 'var(--color-text)'}
        />
      ) : (
        <path
          d="M13.752 7.32V5.816h-5.76V.056H6.488v5.76H.696V7.32h5.792v5.792h1.504V7.32h5.76z"
          fill={color || 'var(--color-text)'}
        />
      )}
    </svg>
  )
}

export const Picker = ({
  text,
  subtext,
  value = 0,
  min = 0,
  max,
  disableIncrement,
  disableDecrement,
  hasError,
  onChange,
  increaseAriaLabel,
  decreaseAriaLabel,
  isCompact,
  className,
  iconColor,
  onDisabledIncrementClick,
}: PickerProps) => {
  const incrementDisabled = disableIncrement || (max != null && value >= max)
  const decrementDisabled = disableDecrement || value <= min

  const _onClickIncrement = () => {
    if (incrementDisabled) {
      if (onDisabledIncrementClick) {
        onDisabledIncrementClick(value)
      }
      return
    }
    return onChange(value + 1)
  }

  const _onClickDecrement = () => {
    if (decrementDisabled) {
      return
    }
    return onChange(value - 1)
  }

  const increaseAria = increaseAriaLabel || `increase number of ${text}`
  const decreaseAria = decreaseAriaLabel || `decrease number of ${text}`
  return (
    <Container hasError={hasError} isCompact={isCompact} className={className}>
      {text && (
        <TextWrap isCompact={isCompact}>
          <TextLabel preset="label" isCompact={isCompact}>
            {text}
          </TextLabel>
          {!isCompact && subtext && <Type preset="small">{subtext}</Type>}
        </TextWrap>
      )}
      <ButtonWrap isCompact={isCompact}>
        <Button
          aria-label={decreaseAria}
          onClick={_onClickDecrement}
          isDisabled={decrementDisabled}
          disabled={decrementDisabled}
          data-test="picker-decrement"
          isCompact={isCompact}
          type="button"
        >
          <Icon
            type="minus"
            color={iconColor}
            width={isCompact ? 11 : scaleSize(14)}
            height={isCompact ? 1 : scaleSize(3)}
          />
        </Button>
        <CountWrap isCompact={isCompact}>
          <Text
            preset="text"
            data-test="picker-value"
            aria-live="polite"
            isCompact={isCompact}
          >
            {value}
          </Text>
        </CountWrap>
        <Button
          aria-label={increaseAria}
          onClick={_onClickIncrement}
          isDisabled={incrementDisabled}
          data-test="picker-increment"
          isCompact={isCompact}
          type="button"
        >
          <Icon
            type="plus"
            color={iconColor}
            width={isCompact ? 11 : scaleSize(14)}
            height={isCompact ? 11 : scaleSize(14)}
          />
        </Button>
      </ButtonWrap>
    </Container>
  )
}
